import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { auth } from '../services/firebase';
import { Spinner, showToast } from '../components/UI';
import FeedbackPanel from './FeedbackPanel';

/* ── Load marked.js from CDN ── */
function useMarked() {
  const [ready, setReady] = useState(!!window.marked);
  useEffect(() => {
    if (window.marked) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/marked/9.1.6/marked.min.js';
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, []);
  return ready;
}

function useIsMobile(breakpoint = 900) {
  const getIsMobile = () => window.innerWidth < breakpoint;
  const [isMobile, setIsMobile] = useState(getIsMobile);
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const TYPE_META = {
  site:     { icon: '🌐', label: 'Website',      color: '#378add' },
  spec:     { icon: '📐', label: 'Product Spec', color: '#8b5cf6' },
  content:  { icon: '📝', label: 'Content',      color: '#5dcaa5' },
  research: { icon: '🔍', label: 'Research',     color: '#e8a87c' },
  strategy: { icon: '📋', label: 'Strategy',     color: '#0e0e0d' },
  campaign: { icon: '📣', label: 'Campaign',     color: '#c8563a' },
};

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ═══════════════════════════════════════════════════════════ */
export default function ArtifactPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const markedReady = useMarked();
  const isMobile = useIsMobile();

  const [artifact, setArtifact] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Firebase UID of the logged-in user — used to show edit/delete on own feedback
  const currentUserId = auth.currentUser?.uid ?? null;

  const fetchArtifact = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getArtifact(id);
      setArtifact(data);
    } catch (e) {
      setError(e.message || 'Failed to load artifact');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchArtifact(); }, [fetchArtifact]);

  useEffect(() => {
    if (!artifact?.workspace_id) return;
    api.getArtifactVersions(artifact.workspace_id, id)
      .then(setVersions)
      .catch(() => {});
  }, [artifact?.workspace_id, id]);

  if (loading) return <div style={centeredStyle}><Spinner size={28} /></div>;

  if (error || !artifact) {
    return (
      <div style={centeredStyle}>
        <p style={{ color: 'var(--ink-mute)', marginBottom: '16px' }}>{error || 'Artifact not found'}</p>
        <button onClick={() => navigate('/dashboard/ceo-runlog')} style={ghostBtnStyle}>← Back to CEO Report</button>
      </div>
    );
  }

  const meta = TYPE_META[artifact.type] || { icon: '📄', label: artifact.type, color: 'var(--ink-mute)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--paper)' }}>
      {/* ── Header ── */}
      <header
        style={{
          minHeight:    isMobile ? '56px' : '52px',
          background:   'rgba(245,242,235,0.92)',
          backdropFilter:'blur(10px)',
          borderBottom: '1px solid var(--paper-3)',
          display:      'flex',
          alignItems:   'center',
          justifyContent:'space-between',
          padding:      isMobile ? '10px 14px' : '0 24px',
          gap:          '12px',
          position:     'sticky',
          top:          0,
          zIndex:       50,
          flexWrap:     isMobile ? 'wrap' : 'nowrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <button onClick={() => navigate('/dashboard/ceo-runlog')} style={ghostBtnStyle}>← CEO Report</button>
          <div style={{ width: '1px', height: '20px', background: 'var(--paper-3)', flexShrink: 0 }} />
          <span
            style={{
              fontSize: '14px', fontWeight: '500', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {artifact.title}
          </span>
        </div>
        {!isMobile && artifact.live_url && (
          <a
            href={artifact.live_url}
            target="_blank"
            rel="noreferrer"
            style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'6px 14px', background:'var(--white)',
              border:'1px solid var(--paper-3)', borderRadius:'var(--r)',
              fontSize:'12px', fontWeight:'500', color:'var(--ink)', textDecoration:'none',
            }}
          >
            🌐 Open site
          </a>
        )}
      </header>

      {/* ── Body ── */}
      <div
        style={{
          display:             'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 260px',
          flex:                1,
          minHeight:           0,
        }}
      >
        {/* ── Main content ── */}
        <main
          style={{
            padding:   isMobile ? '16px 14px 24px' : '24px 40px 32px',
            overflowY: 'auto',
            minWidth:  0,
          }}
        >
          <PublishingConsole url={artifact.live_url} title={artifact.title} isMobile={isMobile} />

          {artifact.type === 'site'
            ? <SitePreview artifact={artifact} isMobile={isMobile} />
            : <MarkdownPreview body={artifact.body} markedReady={markedReady} isMobile={isMobile} />
          }

          {/* ── Mobile: FeedbackPanel floats below the artifact ── */}
          {isMobile && (
            <FeedbackPanel
              entityType="artifact"
              entityId={id}
              currentUserId={currentUserId}
              isMobile={true}
            />
          )}
        </main>

        {/* ── Sidebar ── */}
        <aside
          style={{
            borderLeft: isMobile ? 'none' : '1px solid var(--paper-3)',
            borderTop:  isMobile ? '1px solid var(--paper-3)' : 'none',
            padding:    isMobile ? '20px 14px 28px' : '24px 20px',
            overflowY:  'auto',
            display:    'flex',
            flexDirection:'column',
            gap:        '24px',
            background: isMobile ? 'rgba(255,255,255,0.3)' : 'transparent',
          }}
        >
          {/* ── Desktop: FeedbackPanel at the TOP of the sidebar ── */}
          {!isMobile && (
            <FeedbackPanel
              entityType="artifact"
              entityId={id}
              currentUserId={currentUserId}
              isMobile={false}
            />
          )}

          {/* ── Divider between feedback and meta ── */}
          {!isMobile && (
            <div style={{ height: '1px', background: 'var(--paper-3)', margin: '0 -20px' }} />
          )}

          {/* Type + title */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '20px' }}>{meta.icon}</span>
              <span
                style={{
                  fontSize:'11px', fontWeight:'600', padding:'2px 8px',
                  borderRadius:'100px', background:`${meta.color}20`, color:meta.color,
                  textTransform:'uppercase', letterSpacing:'0.05em',
                }}
              >
                {meta.label}
              </span>
            </div>
            <h1 style={{ fontFamily:'var(--serif)', fontSize:'18px', fontWeight:'400', lineHeight:1.4, margin:0 }}>
              {artifact.title}
            </h1>
          </div>

          {isMobile && artifact.live_url && (
            <MetaSection label="Live site">
              <a
                href={artifact.live_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display:'inline-flex', alignItems:'center', gap:'6px',
                  padding:'8px 12px', background:'var(--white)',
                  border:'1px solid var(--paper-3)', borderRadius:'12px',
                  fontSize:'12px', fontWeight:'500', color:'var(--ink)',
                  textDecoration:'none', width:'fit-content',
                }}
              >
                🌐 Open site
              </a>
            </MetaSection>
          )}

          {/* Meta */}
          <MetaSection label="Details">
            <MetaRow label="Version" value={`v${artifact.version}`} />
            <MetaRow label="Agent"   value={artifact.agent_type || '—'} />
            <MetaRow label="Created" value={formatDate(artifact.created_at)} />
            <MetaRow label="Latest"  value={artifact.is_latest ? 'Yes' : 'No'} />
            {artifact.live_url && (
              <MetaRow
                label="URL"
                value={
                  <a
                    href={artifact.live_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color:'var(--blue)', fontSize:'11px', wordBreak:'break-all', textDecoration:'none' }}
                  >
                    {artifact.live_url}
                  </a>
                }
              />
            )}
          </MetaSection>

          {/* Version history */}
          {versions.length > 1 && (
            <MetaSection label="Version history">
              {versions.map(v => (
                <Link
                  key={v.id}
                  to={`/dashboard/artifacts/${v.id}`}
                  style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    gap:'8px', padding:'8px 10px', borderRadius:'10px', fontSize:'12px',
                    color: v.id === artifact.id ? 'var(--ink)' : 'var(--ink-soft)',
                    background: v.id === artifact.id ? 'var(--paper-3)' : 'transparent',
                    fontWeight: v.id === artifact.id ? '500' : '400',
                    textDecoration:'none', flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                >
                  <span>v{v.version}</span>
                  <span style={{ fontSize:'11px', color:'var(--ink-mute)' }}>
                    {new Date(v.created_at).toLocaleDateString()}
                  </span>
                  {v.is_latest && (
                    <span style={{ fontSize:'10px', color:'var(--teal)', fontWeight:'600' }}>latest</span>
                  )}
                </Link>
              ))}
            </MetaSection>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ── Sub-components (unchanged) ── */

function PublishingConsole({ url, title, isMobile }) {
  if (!url) return null;
  const encodedUrl  = encodeURIComponent(url);
  const safeTitle   = title || 'Check this out';
  const shareTargets = {
    linkedin:  `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook:  `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp:  `https://wa.me/?text=${encodeURIComponent(`${safeTitle} ${url}`)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodeURIComponent(safeTitle)}`,
    x:         `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(safeTitle)}`,
    reddit:    `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(safeTitle)}`,
    email:     `mailto:?subject=${encodeURIComponent(safeTitle)}&body=${encodeURIComponent(`${safeTitle}\n\n${url}`)}`,
  };
  const openPopup = (targetUrl) => window.open(targetUrl, '_blank', 'noopener,noreferrer,width=760,height=680');
  const copyLink  = async () => {
    try { await navigator.clipboard.writeText(url); showToast('Link copied'); }
    catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      showToast('Link copied');
    }
  };

  const containerStyle = { position: isMobile ? 'relative' : 'sticky', top: isMobile ? 'auto' : '68px', zIndex: 25, marginBottom: isMobile ? '16px' : '22px' };
  const panelStyle = { borderRadius:'5px', padding: isMobile ? '16px 14px 14px' : '18px 18px 16px', border:'1px solid rgba(93,202,165,0.28)', background:'linear-gradient(135deg, rgba(12,52,47,0.96) 0%, rgba(21,86,77,0.96) 100%)', boxShadow:'0 18px 40px rgba(10,30,26,0.24)', color:'var(--white)', overflow:'hidden' };
  const buttonStyle = { display:'inline-flex', alignItems:'center', justifyContent:'center', gap:'8px', padding: isMobile ? '10px 12px' : '10px 14px', borderRadius:'999px', border:'1px solid rgba(255,255,255,0.14)', background:'rgba(255,255,255,0.12)', color:'var(--white)', cursor:'pointer', fontSize:'12px', fontWeight:'600', whiteSpace:'nowrap', backdropFilter:'blur(8px)', minHeight:'40px', textDecoration:'none' };

  return (
    <div style={containerStyle}>
      <div style={panelStyle}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', marginBottom:'14px', flexWrap:'wrap' }}>
          <div>
            <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight:'700', lineHeight:1.2, margin:0, color:'var(--white)' }}>Publishing Console</h2>
            <p style={{ margin:'6px 0 0', fontSize: isMobile ? '12px' : '13px', lineHeight:1.5, color:'rgba(255,255,255,0.82)' }}>Distribute this live preview across your channels.</p>
          </div>
          {!isMobile && <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'7px 10px', borderRadius:'999px', background:'rgba(255,255,255,0.10)', border:'1px solid rgba(255,255,255,0.12)', fontSize:'11px', fontWeight:'600', color:'rgba(255,255,255,0.9)', whiteSpace:'nowrap' }}>🌐 Live preview ready</div>}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'10px' }}>
          {[
            ['💼', 'LinkedIn', shareTargets.linkedin],
            ['📘', 'Facebook', shareTargets.facebook],
            ['💬', 'WhatsApp', shareTargets.whatsapp],
            ['📌', 'Pinterest', shareTargets.pinterest],
            ['✖️', 'X', shareTargets.x],
            ['👽', 'Reddit', shareTargets.reddit],
          ].map(([icon, label, target]) => (
            <button key={label} type="button" onClick={() => openPopup(target)} style={buttonStyle}><span>{icon}</span><span>{label}</span></button>
          ))}
          <button type="button" onClick={() => { window.location.href = shareTargets.email; }} style={buttonStyle}><span>✉️</span><span>Email</span></button>
          <button type="button" onClick={copyLink} style={buttonStyle}><span>🔗</span><span>Copy Link</span></button>
        </div>
      </div>
    </div>
  );
}

function SitePreview({ artifact, isMobile }) {
  return (
    <div>
      <div style={{ marginBottom:'42px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
        <span style={{ fontSize:'13px', color:'var(--ink-soft)' }}>Live preview</span>
        {artifact.live_url && (
          <a href={artifact.live_url} target="_blank" rel="noreferrer" style={{ fontSize:'12px', color:'var(--blue)', wordBreak:'break-all', textDecoration:'none' }}>
            {artifact.live_url} ↗
          </a>
        )}
      </div>
      <div style={{ border:'1px solid var(--paper-3)', borderRadius:'var(--r)', overflow:'hidden', background:'var(--white)' }}>
        {artifact.live_url ? (
          <iframe src={artifact.live_url} title={artifact.title} style={{ width:'100%', height: isMobile ? '70vh' : '600px', border:'none', display:'block' }} sandbox="allow-scripts allow-same-origin" />
        ) : artifact.body ? (
          <iframe srcDoc={artifact.body} title={artifact.title} style={{ width:'100%', height: isMobile ? '70vh' : '600px', border:'none', display:'block' }} sandbox="allow-scripts" />
        ) : (
          <div style={{ padding:'40px', textAlign:'center', color:'var(--ink-mute)' }}>Site content not yet available</div>
        )}
      </div>
    </div>
  );
}

function MarkdownPreview({ body, markedReady, isMobile }) {
  if (!body) return <div style={{ color:'var(--ink-mute)', fontSize:'14px' }}>No content available.</div>;
  if (!markedReady) return <pre style={{ fontFamily:'monospace', fontSize:'13px', lineHeight:1.6, whiteSpace:'pre-wrap', overflowX:'auto' }}>{body}</pre>;
  const html = window.marked.parse(body, { gfm: true, breaks: true });
  return (
    <div
      style={{ fontFamily:'var(--sans)', fontSize:'14px', lineHeight:'1.7', color:'var(--ink)', maxWidth: isMobile ? '100%' : '780px', overflowWrap:'anywhere' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function MetaSection({ label, children }) {
  return (
    <div>
      <div style={{ fontSize:'10px', fontWeight:'600', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--ink-mute)', marginBottom:'10px', paddingBottom:'6px', borderBottom:'1px solid var(--paper-3)' }}>
        {label}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>{children}</div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'8px' }}>
      <span style={{ fontSize:'11px', color:'var(--ink-mute)', flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:'12px', fontWeight:'500', textAlign:'right', minWidth:0 }}>{value}</span>
    </div>
  );
}

const centeredStyle = { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:'12px' };
const ghostBtnStyle = { background:'none', border:'none', cursor:'pointer', fontSize:'13px', color:'var(--ink-soft)', padding:'4px 0' };